import React, { useEffect, useRef, useState } from 'react';
import styles from './CategoryTree.module.less';
import PropTypes from 'prop-types';
import CategoryTreeItem from './CategoryTreeItem';
import Icon from '../../Icon';
import clsx from 'clsx';
import CategoryTreeBreadcrumb from './CategoryTreeBreadcrumb';

const CategoryTree = ({
  selectedCategory,
  viableCategories,
  onClick,
  contentRequestInProcess,
}) => {
  let containerRef = useRef(null);
  let collapseBtnDivRef = useRef(null);
  const [isCollapsed, setCollapsed] = useState(true);
  const isMobileWidth = (width = window.innerWidth) => {
    return width <= 870;
  };

  useEffect(() => {
    calculateContainerLayout();
    // For mobile width, expanded is the default
    setCollapsed(!isMobileWidth());
  }, []);

  const breadcrumbs = [
    {
      display: 'All Subjects',
      href: '/resources/',
    },
  ];

  // Build breadcrumb and identify category items to display
  let currentNode = viableCategories;
  if (selectedCategory.text !== 'root') {
    currentNode = currentNode.children;
    // Traverse until we hit an array (leaf node)
    while (!Array.isArray(currentNode)) {
      // Add current node to breadcrumbs
      breadcrumbs.push({
        display: currentNode.text,
        href: currentNode.href,
      });
      // Move to next node
      currentNode = currentNode.children;
    }
  }
  // do not display categories with no content
  const categoryItems = currentNode.filter(item => item.count > 0);

  const handleClickCollapse = function(e) {
    if (e.type !== 'click' && e.key !== 'Enter') {
      return;
    }

    setCollapsed(prevState => !prevState);
  };

  const calculateContainerLayout = () => {
    if (!containerRef.current.children.length) return;
    /** @type HTMLElement **/
    const container = containerRef.current;
    const computedStyles = window.getComputedStyle(container);
    const paddingX = parseInt(computedStyles.paddingLeft) * 2;
    const targetWidth = container.clientWidth - paddingX;
    const defaultColumnGap = 22;

    // Measure natural content widths, since we set 'width: calc(100% - 28px)' for displaying wider hover area
    // We need to measure the clone to get the natural width
    const childWidths = Array.from(container.children).map(li => {
      const subCategory = li.children[0];

      // Create invisible clone with natural width
      const clone = subCategory.cloneNode(true);
      clone.style.cssText = `
        position: absolute;
        visibility: hidden;
        width: max-content;
        max-width: none;
        white-space: nowrap;
      `;
      document.body.appendChild(clone);
      const width = clone.getBoundingClientRect().width;
      document.body.removeChild(clone);

      return width;
    });

    // Calculate optimal column count
    const childCount = container.children.length;
    const minChildWidth = Math.min(...childWidths);
    const maxPossibleCols = Math.min(
      childCount,
      Math.floor(
        (targetWidth + defaultColumnGap) / (minChildWidth + defaultColumnGap)
      )
    );

    let finalColCount = 1;
    for (let colCount = maxPossibleCols; colCount >= 1; colCount--) {
      const columns = Array.from({ length: colCount }, (_, col) =>
        Math.max(...childWidths.filter((_, idx) => idx % colCount === col))
      );
      const totalWidth =
        columns.reduce((a, b) => a + b, 0) + (colCount - 1) * defaultColumnGap;

      if (totalWidth <= targetWidth || colCount === 1) {
        finalColCount = colCount;
        break;
      }
    }

    // Apply grid layout
    container.style.gridTemplateColumns = `repeat(${finalColCount}, max-content)`;

    // Collapse button logic
    const rowCount = Math.ceil(childCount / finalColCount);
    const collapseBtnDiv = collapseBtnDivRef.current;
    if (collapseBtnDiv) {
      collapseBtnDiv.classList.toggle(
        'hidden',
        !(rowCount > 4 || isMobileWidth())
      );
    }
  };

  const [layoutReady, setLayoutReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    calculateContainerLayout();
    setLayoutReady(true);
  }, [categoryItems, isCollapsed]);

  const scrollSelectedIntoView = () => {
    const container = containerRef.current;
    if (container && selectedCategory?.children?.length === 0) {
      const selectedListItem = container.querySelector(
        `li[data-selected=true]`
      );
      if (selectedListItem) {
        container.scrollTop = selectedListItem.offsetTop - 10;
      }
    }
  };

  useEffect(() => {
    scrollSelectedIntoView();
  }, [selectedCategory, isCollapsed]);

  const handleResize = prevWidth => {
    if (!isMobileWidth(prevWidth) && isMobileWidth()) {
      // For mobile width, expanded is the default
      setCollapsed(false);
    }
    calculateContainerLayout();
    scrollSelectedIntoView();
  };

  useEffect(() => {
    const prevWidth = window.innerWidth;
    const resizeHandler = () => {
      handleResize(prevWidth);
    };
    window.addEventListener('resize', resizeHandler);
    return () => window.removeEventListener('resize', resizeHandler);
  }, []);

  let iconName;
  let toggleLabel;
  if (isCollapsed) {
    iconName = 'angle-down';
    toggleLabel = 'Show More';
  } else {
    iconName = 'angle-up';
    toggleLabel = 'Show Less';
  }

  return (
    <div className={styles.container}>
      <CategoryTreeBreadcrumb
        breadcrumbNodes={breadcrumbs}
        onClick={onClick}
        contentRequestInProcess={contentRequestInProcess}
      ></CategoryTreeBreadcrumb>
      <ul
        className={clsx(
          styles.subCategoryContainer,
          isCollapsed && styles.collapsed,
          !layoutReady && styles.invisible
        )}
        ref={containerRef}
        data-cy="category-container"
      >
        {categoryItems.map(category => (
          <li
            key={category.urlSlice}
            data-selected={category.urlSlice === selectedCategory.urlSlice}
          >
            <CategoryTreeItem
              key={category.urlSlice}
              displayName={category.text}
              href={category.href}
              urlSlice={category.urlSlice}
              selected={category.urlSlice === selectedCategory.urlSlice}
              onClick={onClick}
              hasChildren={category.children?.length > 0}
              contentRequestInProcess={contentRequestInProcess}
            >
              {category.text}
            </CategoryTreeItem>
          </li>
        ))}
      </ul>
      <div
        className={clsx(styles.collapseBtnDiv, styles.hidden)}
        ref={collapseBtnDivRef}
      >
        <button className={styles.collapseBtn} onClick={handleClickCollapse}>
          {toggleLabel}
          <Icon name={iconName} />
        </button>
      </div>
    </div>
  );
};

export default CategoryTree;

const taxonomyCategoryPropTypes = {
  children: PropTypes.arrayOf(PropTypes.shape(this)),
  urlSlice: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  visible: PropTypes.bool.isRequired,
  shortCategoryName: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
};

const viableCategoriesPropTypes = {
  urlSlice: PropTypes.string.isRequired,
  count: PropTypes.number.isRequired,
  text: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape(this)),
    PropTypes.shape(this),
  ]),
};

CategoryTree.propTypes = {
  selectedCategory: PropTypes.shape(taxonomyCategoryPropTypes).isRequired,
  viableCategories: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.shape(viableCategoriesPropTypes)),
    PropTypes.shape(viableCategoriesPropTypes),
  ]),
  onClick: PropTypes.func,
  contentRequestInProcess: PropTypes.bool.isRequired,
};
